(() => {
  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  const mediaGroupForColour = (colour) => normalize(colour);

  const galleryOrder = (src, originalIndex = 0) => {
    const source = String(src ?? '').split('?')[0].toLowerCase();
    const matches = [...source.matchAll(/-(0[1-9]|[1-9][0-9])(?=-|\.[a-z0-9]+$)/g)];
    const numberedPosition = matches.length
      ? Number(matches[matches.length - 1][1])
      : NaN;
    return Number.isFinite(numberedPosition)
      ? numberedPosition
      : 1000 + Number(originalIndex || 0);
  };

  const variantForOptions = (variants, selectedOptions) => variants.find((variant) => (
    Array.isArray(variant.options)
      && variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => (
        normalize(value) === normalize(selectedOptions[index])
      ))
  ));

  const mediaBelongsToGroup = (mediaGroup, activeGroup) => {
    const group = normalize(mediaGroup);
    const active = normalize(activeGroup);
    return active ? group === active : group === 'all';
  };

  const mediaLabel = (mediaType, position, total) => {
    const type = normalize(mediaType);
    const kind = type === 'video' || type === 'external_video' ? 'video' : 'image';
    return `Show ${kind} ${position} of ${total}`;
  };

  const pauseMedia = (item) => {
    if (!item) return;

    item.querySelectorAll?.('video').forEach((video) => {
      video.pause?.();
    });
    item.querySelectorAll?.('iframe').forEach((iframe) => {
      const source = String(iframe.src || iframe.getAttribute?.('src') || '').toLowerCase();
      const message = source.includes('vimeo')
        ? JSON.stringify({ method: 'pause' })
        : JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] });
      iframe.contentWindow?.postMessage?.(message, '*');
    });
  };

  const purchasePrice = (variant, offerPrice, burgundyIds) => {
    if (offerPrice > 0) return offerPrice;
    return burgundyIds.has(String(variant.id))
      ? Math.round(variant.price * 0.8)
      : variant.price;
  };

  const initializedRoots = new WeakSet();

  const initRoot = (root) => {
    let product;
    let selectedOptions;

    try {
      product = JSON.parse(root.dataset.productJson || '{}');
      selectedOptions = JSON.parse(root.dataset.selectedOptions || '[]');
    } catch {
      return;
    }

    if (!Array.isArray(product.variants) || !Array.isArray(selectedOptions)) return;
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const optionName = (option) => (
      typeof option === 'string' ? option : option?.name || ''
    );
    const productOptions = Array.isArray(product.options) ? product.options : [];
    const currency = root.dataset.currency || 'GBP';
    const form = root.querySelector('[data-pdp-form]');
    const variantInput = root.querySelector('[data-pdp-variant-id]');
    const quantityInput = form?.querySelector('[name="quantity"]') || null;
    const addButton = form?.querySelector('.fw-pdp-add') || null;
    const mobileButton = root.querySelector('[data-pdp-action="mobile-add"]');
    const status = root.querySelector('[data-pdp-status]');
    const price = root.querySelector('[data-pdp-price]');
    const originalPrice = root.querySelector('[data-pdp-original-price]');
    const burgundyIds = new Set((root.dataset.burgundyDiscountIds || '').split(','));
    const offerPrice = Math.max(0, Number(root.dataset.offerPrice || 0));
    const galleryTrack = root.querySelector('[data-gallery-track]');
    const movementImage = root.querySelector('img[id^="FormwynnMovementImage-"]');
    const zoom = root.querySelector('[data-pdp-zoom]');
    const sizeGuide = root.querySelector('[data-pdp-size-guide]');
    const colourIndex = productOptions.findIndex((option) => /colou?r/i.test(optionName(option)));
    let selectedVariant = product.variants.find((variant) => (
      variantInput && String(variant.id) === String(variantInput.value)
    )) || variantForOptions(product.variants, selectedOptions)
      || product.variants.find((variant) => variant.available)
      || product.variants[0];
    let activeMediaGroup = colourIndex >= 0
      ? mediaGroupForColour(selectedOptions[colourIndex])
      : '';
    if (selectedVariant && selectedOptions.length !== selectedVariant.options.length) {
      selectedOptions = [...selectedVariant.options];
      activeMediaGroup = colourIndex >= 0
        ? mediaGroupForColour(selectedOptions[colourIndex])
        : '';
    }

    const money = (amount) => {
      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency,
        }).format(Number(amount || 0) / 100);
      } catch {
        return `${currency} ${(Number(amount || 0) / 100).toFixed(2)}`;
      }
    };

    const moneyElement = (amount) => {
      const element = document.createElement('span');
      element.className = 'money';
      element.textContent = money(amount);
      return element;
    };

    const reconvertMoney = () => globalThis.bucksCC?.reConvert?.();
    const currencySlot = root.querySelector('[data-pdp-currency-slot]');
    const mountCurrencySelector = () => {
      const widget = document.querySelector('.buckscc-currency-wrapper');
      if (!widget || !currencySlot || !root.isConnected) return;
      if (widget.parentElement !== currencySlot) {
        currencySlot.append(widget);
      }
    };
    const currencyReady = () => {
      mountCurrencySelector();
      reconvertMoney();
    };
    const currencyObserver = new MutationObserver(mountCurrencySelector);
    currencyObserver.observe(document.body, { childList: true });
    globalThis.addEventListener('BUCKSCC-LOADED', currencyReady);
    const purchaseObserver = new IntersectionObserver((entries) => {
      document.body.classList.toggle('fw-pdp-purchase-in-view', entries[0].isIntersecting);
    }, { rootMargin: '-64px 0px -88px 0px' });
    if (form) purchaseObserver.observe(form);
    const sectionUnload = (event) => {
      if (event.target !== root && !event.target.contains?.(root)) return;
      const widget = currencySlot?.querySelector('.buckscc-currency-wrapper');
      if (widget) document.body.append(widget);
      currencyObserver.disconnect();
      purchaseObserver.disconnect();
      document.body.classList.remove('fw-pdp-purchase-in-view');
      globalThis.removeEventListener('BUCKSCC-LOADED', currencyReady);
      document.removeEventListener('shopify:section:unload', sectionUnload);
    };
    document.addEventListener('shopify:section:unload', sectionUnload);
    mountCurrencySelector();

    function sortThumbnails() {
      const sorted = [...root.querySelectorAll('[data-pdp-action="thumbnail"][data-media-group]')]
        .map((thumbnail, index) => {
          const originalIndex = Number(thumbnail.dataset.originalIndex ?? index);
          const order = Number(thumbnail.dataset.galleryOrder)
            || galleryOrder(thumbnail.dataset.srcKey || thumbnail.dataset.src, originalIndex);
          thumbnail.dataset.galleryOrder = String(order);
          thumbnail.style.order = String(order);
          return { thumbnail, order, index };
        })
        .sort((a, b) => a.order - b.order || a.index - b.index)
        .map(({ thumbnail }) => thumbnail);

      if (
        galleryTrack
        && sorted.some((thumbnail, index) => galleryTrack.children[index] !== thumbnail)
      ) {
        sorted.forEach((thumbnail) => galleryTrack.append(thumbnail));
      }

      return sorted;
    }

    const visibleFrom = (thumbnails) => {
      const exact = activeMediaGroup
        ? thumbnails.filter((thumbnail) => mediaBelongsToGroup(
          thumbnail.dataset.mediaGroup,
          activeMediaGroup,
        ))
        : [];
      if (exact.length) return exact;
      return thumbnails.filter((thumbnail) => normalize(thumbnail.dataset.mediaGroup) === 'all');
    };

    const visibleThumbnails = () => visibleFrom(sortThumbnails());

    function setStatus(message) {
      if (status) status.textContent = message;
    }

    function setButtonState(button, text, disabled, amount) {
      if (!button) return;
      if (typeof amount === 'number') {
        button.replaceChildren(document.createTextNode('Add to bag - '), moneyElement(amount));
      } else {
        button.textContent = text;
      }
      button.disabled = disabled;
      button.removeAttribute('aria-label');
    }

    function updateGalleryCount(thumbnails, activeThumbnail) {
      const position = root.querySelector('[data-gallery-position]');
      if (!position) return;

      const index = activeThumbnail ? thumbnails.indexOf(activeThumbnail) : -1;
      position.textContent = String(index >= 0 ? index + 1 : (thumbnails.length ? 1 : 0));
      const total = root.querySelector('[data-gallery-total]');
      if (total) total.textContent = String(thumbnails.length);
    }

    function syncOptionButtons() {
      root.querySelectorAll('.fw-pdp-option[data-option-index]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-pdp-action="option"]').forEach((button) => {
          const active = normalize(button.dataset.optionValue)
            === normalize(selectedOptions[index]);
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        const label = group.querySelector('[data-option-value]');
        if (label) label.textContent = selectedOptions[index] || '';
      });
    }

    function updateMobileBar() {
      const selection = root.querySelector('[data-mobile-selection]');
      if (selection) {
        selection.textContent = selectedOptions.filter(Boolean).join(' / ');
      }
    }

    function setActiveThumbnail(thumbnail) {
      if (!thumbnail) return;

      const thumbnails = visibleThumbnails();
      thumbnails.forEach((item) => {
        const active = item === thumbnail;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
        if (!active) pauseMedia(item);
      });

      updateGalleryCount(thumbnails, thumbnail);
    }

    function syncGalleryAccessibility(thumbnails) {
      const visible = visibleFrom(thumbnails);

      thumbnails.forEach((thumbnail) => {
        const visibleIndex = visible.indexOf(thumbnail);
        if (visibleIndex >= 0) {
          thumbnail.dataset.mediaIndex = String(visibleIndex + 1);
          thumbnail.setAttribute(
            'aria-label',
            mediaLabel(thumbnail.dataset.mediaType, visibleIndex + 1, visible.length),
          );
          thumbnail.removeAttribute('aria-hidden');
        } else {
          delete thumbnail.dataset.mediaIndex;
          thumbnail.removeAttribute('aria-label');
          thumbnail.setAttribute('aria-hidden', 'true');
        }
      });

      return visible;
    }

    function setActiveColourGallery(colour, preferredMediaId, preserveActive = false) {
      activeMediaGroup = mediaGroupForColour(colour);
      const thumbnails = sortThumbnails();
      const visibleItems = visibleFrom(thumbnails);

      thumbnails.forEach((thumbnail) => {
        const visible = visibleItems.includes(thumbnail);
        thumbnail.classList.toggle('is-hidden', !visible);
        if (!visible) {
          pauseMedia(thumbnail);
          thumbnail.classList.remove('is-active');
          thumbnail.setAttribute('aria-pressed', 'false');
        }
      });
      const visible = syncGalleryAccessibility(thumbnails);

      const colourLabel = root.querySelector('[data-gallery-colour]');
      if (colourLabel) colourLabel.textContent = colour || '';

      const existing = visible.find((thumbnail) => thumbnail.classList.contains('is-active'));
      const preferred = visible.find((thumbnail) => (
        String(thumbnail.dataset.mediaId) === String(preferredMediaId)
      ));
      const next = preserveActive
        ? existing || preferred || visible[0]
        : visible[0];

      if (next) {
        setActiveThumbnail(next);
        if (!preserveActive) {
          galleryTrack?.scrollTo?.({ left: 0, behavior: 'auto' });
        }
      } else {
        updateGalleryCount(visible, null);
      }
    }

    function updatePurchaseState({ preserveStatus = false } = {}) {
      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      updateMobileBar();

      if (!selectedVariant) {
        if (variantInput) variantInput.value = '';
        setButtonState(addButton, 'Unavailable', true);
        setButtonState(mobileButton, 'Unavailable', true);
        if (!preserveStatus) setStatus('This combination is unavailable.');
        return;
      }

      if (variantInput) variantInput.value = selectedVariant.id;
      const displayUnitPrice = purchasePrice(selectedVariant, offerPrice, burgundyIds);
      if (originalPrice) {
        originalPrice.hidden = displayUnitPrice >= selectedVariant.price;
        originalPrice.replaceChildren(moneyElement(selectedVariant.price));
      }
      if (price) price.replaceChildren(moneyElement(displayUnitPrice));

      const available = Boolean(selectedVariant.available);
      const buttonText = available
        ? `Add to bag - ${money(displayUnitPrice * quantity)}`
        : 'Sold out';
      const buttonAmount = available ? displayUnitPrice * quantity : undefined;
      setButtonState(addButton, buttonText, !available, buttonAmount);
      setButtonState(mobileButton, buttonText, !available, buttonAmount);
      reconvertMoney();
      if (!preserveStatus) {
        setStatus(available ? '' : 'This selection is sold out.');
      }
    }

    function syncVariant({ preserveGallery = false } = {}) {
      selectedVariant = variantForOptions(product.variants, selectedOptions);
      syncOptionButtons();
      updatePurchaseState();

      const colour = colourIndex >= 0 ? selectedOptions[colourIndex] : '';
      const preferredMediaId = selectedVariant?.featured_image?.id
        ?? selectedVariant?.featured_media?.id;
      setActiveColourGallery(colour, preferredMediaId, preserveGallery);
    }

    function moveThumbnail(delta) {
      const thumbnails = visibleThumbnails();
      if (!thumbnails.length) return;
      const currentIndex = thumbnails.findIndex((item) => item.classList.contains('is-active'));
      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (startIndex + delta + thumbnails.length) % thumbnails.length;
      const next = thumbnails[nextIndex];
      setActiveThumbnail(next);
      next.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    function openZoom(thumbnail) {
      if (!zoom || !thumbnail) return;
      if (normalize(thumbnail.dataset.mediaType || 'image') !== 'image') return;
      const sourceImage = thumbnail.querySelector?.('img');
      const image = zoom.querySelector('img');
      if (!image || !sourceImage) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = thumbnail.dataset.src || sourceImage.src || sourceImage.currentSrc;
      image.alt = sourceImage.alt || thumbnail.dataset.alt || product.title || '';
      zoom.showModal?.();
    }

    function selectOption(button) {
      const index = Number(button.dataset.optionIndex);
      if (!Number.isInteger(index) || index < 0 || !button.dataset.optionValue) return;

      selectedOptions[index] = button.dataset.optionValue;
      const group = button.closest('.fw-pdp-option[data-option-index]');
      if (group) {
        group.querySelectorAll('[data-pdp-action="option"]').forEach((option) => {
          const active = normalize(option.dataset.optionValue)
            === normalize(button.dataset.optionValue);
          option.classList.toggle('is-active', active);
          option.setAttribute('aria-pressed', String(active));
        });
        const label = group.querySelector('[data-option-value]');
        if (label) label.textContent = button.dataset.optionValue;
      }

      syncVariant({ preserveGallery: index !== colourIndex });
    }

    function selectStoryColour(button) {
      const index = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      if (!Number.isInteger(index) || index < 0 || !optionValue) return;

      const matchingOption = [...root.querySelectorAll('[data-pdp-action="option"]')]
        .find((option) => (
          Number(option.dataset.optionIndex) === index
          && normalize(option.dataset.optionValue) === normalize(optionValue)
        ));

      if (matchingOption) {
        selectOption(matchingOption);
      } else {
        selectedOptions[index] = optionValue;
        syncVariant();
      }
      root.querySelector('.fw-pdp-purchase-view')?.scrollIntoView({ behavior: 'smooth' });
    }

    function activateMovementTab(button) {
      const movementTabs = [...root.querySelectorAll('[data-pdp-action="movement"]')];
      if (!movementTabs.includes(button)) return;

      if (movementImage && button.dataset.src) {
        movementImage.removeAttribute('srcset');
        movementImage.removeAttribute('sizes');
        movementImage.src = button.dataset.src;
        movementImage.alt = button.dataset.alt || product.title || '';
      }
      movementTabs.forEach((tab) => {
        const active = tab === button;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      const panel = movementImage?.closest('[role="tabpanel"]')
        || root.querySelector('.fw-pdp-movement-test [role="tabpanel"]');
      if (panel && button.id) panel.setAttribute('aria-labelledby', button.id);
    }

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pdp-action]');
      if (!button) return;
      const action = button.dataset.pdpAction;

      if (action === 'option') {
        selectOption(button);
      } else if (action === 'story-colour') {
        selectStoryColour(button);
      } else if (action === 'thumbnail') {
        if (visibleThumbnails().includes(button)) {
          setActiveThumbnail(button);
          openZoom(button);
        }
      } else if (action === 'previous') {
        moveThumbnail(-1);
      } else if (action === 'next') {
        moveThumbnail(1);
      } else if (action === 'quantity-decrease' && quantityInput) {
        quantityInput.value = Math.max(1, Number(quantityInput.value) - 1);
        updatePurchaseState();
      } else if (action === 'quantity-increase' && quantityInput) {
        quantityInput.value = Math.min(10, Number(quantityInput.value) + 1);
        updatePurchaseState();
      } else if (action === 'zoom') {
        openZoom(button);
      } else if (action === 'close-zoom') {
        zoom?.close?.();
      } else if (action === 'size-guide') {
        sizeGuide?.showModal?.();
      } else if (action === 'close-size-guide') {
        sizeGuide?.close?.();
      } else if (action === 'movement') {
        activateMovementTab(button);
      } else if (action === 'offer-details') {
        const panel = root.querySelector('[data-fw-offer-details-panel]');
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        if (panel) panel.hidden = !expanded;
      } else if (action === 'mobile-add' && selectedVariant?.available) {
        form?.requestSubmit?.();
      }
    });

    root.addEventListener('keydown', (event) => {
      const currentTab = event.target.closest('[data-pdp-action="movement"]');
      if (!currentTab) return;

      const movementTabs = [...root.querySelectorAll('[data-pdp-action="movement"]')];
      const currentIndex = movementTabs.indexOf(currentTab);
      if (currentIndex < 0 || movementTabs.length < 2) return;

      let nextIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % movementTabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + movementTabs.length) % movementTabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = movementTabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      movementTabs[nextIndex].focus();
      activateMovementTab(movementTabs[nextIndex]);
    });

    galleryTrack?.addEventListener('scroll', () => {
      const visible = visibleThumbnails();
      if (!visible.length || !Number.isFinite(galleryTrack.scrollLeft)) return;
      const nearest = visible.reduce((closest, thumbnail) => {
        const closestDistance = Math.abs((closest.offsetLeft || 0) - galleryTrack.scrollLeft);
        const distance = Math.abs((thumbnail.offsetLeft || 0) - galleryTrack.scrollLeft);
        return distance < closestDistance ? thumbnail : closest;
      }, visible[0]);
      setActiveThumbnail(nearest);
    }, { passive: true });

    [zoom, sizeGuide].filter(Boolean).forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close?.();
      });
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!selectedVariant?.available) return;

      if (addButton) addButton.disabled = true;
      if (mobileButton) mobileButton.disabled = true;
      setStatus('Adding to bag...');
      try {
        const marketRoot = String(globalThis.Shopify?.routes?.root || '/')
          .replace(/\/+$/, '');
        const cartAddUrl = `${marketRoot}/cart/add.js`;
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: selectedVariant.id,
              quantity: Math.max(1, Number(quantityInput?.value || 1)),
            }],
          }),
        });
        if (!response.ok) throw new Error('Unable to add this selection.');
        setStatus('Added to bag.');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        setStatus(error instanceof Error ? error.message : 'Unable to add this selection.');
      } finally {
        updatePurchaseState({ preserveStatus: true });
      }
    });

    syncOptionButtons();
    syncVariant({ preserveGallery: true });
  };

  const initRootsWithin = (target) => {
    if (!target) return;

    const roots = [];
    if (target.matches?.('[data-formwynn-pdp]')) roots.push(target);
    target.querySelectorAll?.('[data-formwynn-pdp]').forEach((root) => {
      roots.push(root);
    });
    [...new Set(roots)].forEach(initRoot);
  };

  globalThis.FormwynnProductDetail = Object.freeze({
    initRoot,
    purchasePrice,
    galleryOrder,
    mediaGroupForColour,
    variantForOptions,
    mediaBelongsToGroup,
    mediaLabel,
    pauseMedia,
  });

  initRootsWithin(document);
  document.addEventListener('shopify:section:load', (event) => {
    initRootsWithin(event.target);
  });
})();
